import React, { useCallback, useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CInputGroup,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
} from "@coreui/react";
import {
  getTaxPayerAssets,
  createSignatureUploadUrl,
  createLogoUploadUrl,
  createTemplateUploadUrl,
  getSignatureUrl,
  getLogoUrl,
  getTemplateUrl,
  deleteTaxPayerAsset,
  TaxPayerAssetField,
  updateSignaturePassword,
} from "../../api/tax-payer-assets";
import TaxPayerAssets, {
  EMAIL_TEMPLATE_FIELDS,
  KUDE_TEMPLATE_FIELDS,
  TemplateField,
} from "../../models/tax-payer-assets";
import Loading from "../indicators/Loading";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert, successAlert } from "../utils/messages";
import { getObjectMinioUrl, putObjectMinioUrl } from "../../minio/urls";
import { formatUTCDateTime } from "../../utils/dates";

type TabKey = "signature" | "logo" | "templates";

const getRequestDetail = (response: { detail?: string }) =>
  response.detail ?? "Error al guardar la contraseña de firma";

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

interface DropZoneProps {
  label: string;
  currentFileName?: string | null;
  currentPreviewUrl?: string | null;
  accept?: string;
  onFileDrop: (file: File) => void;
  onDownload?: () => Promise<void> | void;
  onClear?: () => void;
  onDelete?: () => Promise<void> | void;
}

const DropZone: React.FC<DropZoneProps> = ({
  label,
  currentFileName,
  currentPreviewUrl,
  accept,
  onFileDrop,
  onDownload,
  onClear,
  onDelete,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPreviewFailed(false);
  }, [currentPreviewUrl]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) onFileDrop(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileDrop(file);
  };

  const handleDownload = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!onDownload) return;

    setDownloading(true);
    try {
      await onDownload();
    } finally {
      setDownloading(false);
    }
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (inputRef.current) inputRef.current.value = "";
    onClear?.();
  };

  const handleDelete = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!onDelete) return;

    setDeleting(true);
    try {
      await onDelete();
    } finally {
      setDeleting(false);
    }
  };

  const showDownload =
    Boolean(currentFileName) &&
    (!currentPreviewUrl || previewFailed) &&
    Boolean(onDownload);

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      style={{
        border: `2px dashed ${isDragging ? "#321fdb" : "#aaaaaa"}`,
        borderRadius: "8px",
        padding: "24px",
        textAlign: "center",
        cursor: "pointer",
        backgroundColor: isDragging ? "var(--theme-highlight)" : "var(--theme-surface-raised)",
        transition: "all 0.2s",
        marginBottom: "12px",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={handleInputChange}
      />
      <p style={{ marginBottom: "4px", fontWeight: 600 }}>{label}</p>
      {currentFileName ? (
        <p style={{ fontSize: "0.85rem", color: "var(--theme-text-soft)", marginBottom: "4px" }}>
          Archivo actual: <strong>{currentFileName}</strong>
        </p>
      ) : (
        <p style={{ fontSize: "0.85rem", color: "var(--theme-muted)", marginBottom: "4px" }}>
          Arrastra un archivo aquí o haz clic para seleccionar
        </p>
      )}
      {currentPreviewUrl && !previewFailed && (
        <img
          src={currentPreviewUrl}
          alt="preview"
          onError={() => setPreviewFailed(true)}
          style={{
            maxHeight: "80px",
            maxWidth: "200px",
            objectFit: "contain",
            marginTop: "8px",
          }}
        />
      )}
      {(showDownload || onClear || onDelete) && (
        <div
          style={{
            marginTop: "8px",
            display: "flex",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          {showDownload && (
            <CButton
              type="button"
              color="secondary"
              variant="outline"
              size="sm"
              disabled={downloading}
              onClick={handleDownload}
            >
              {downloading ? (
                <Spinner size="sm" animation="border" className="mr-1" />
              ) : (
                <i className="fa fa-download mr-1" />
              )}
              Descargar
            </CButton>
          )}
          {onClear && (
            <CButton
              type="button"
              color="danger"
              variant="outline"
              size="sm"
              onClick={handleClear}
            >
              <i className="fa fa-times mr-1" />
              Quitar selección
            </CButton>
          )}
          {onDelete && (
            <CButton
              type="button"
              color="danger"
              size="sm"
              disabled={deleting}
              onClick={handleDelete}
            >
              {deleting ? (
                <Spinner size="sm" animation="border" className="mr-1" />
              ) : (
                <i className="fa fa-trash mr-1" />
              )}
              Eliminar archivo actual
            </CButton>
          )}
        </div>
      )}
    </div>
  );
};

const TaxPayerAssetsForm: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>("signature");

  const [assets, setAssets] = useState<TaxPayerAssets | null>(null);

  // Files staged for upload
  const signatureFile = useRef<File | null>(null);
  const logoFile = useRef<File | null>(null);
  const templateFiles = useRef<Partial<Record<TemplateField, File>>>({});

  // Preview URLs (only for images)
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  // Staged file names (to show in UI before upload)
  const [stagedSignatureName, setStagedSignatureName] = useState<string | null>(
    null,
  );
  const [stagedLogoName, setStagedLogoName] = useState<string | null>(null);
  const [stagedTemplateNames, setStagedTemplateNames] = useState<
    Partial<Record<TemplateField, string>>
  >({});

  // Signature password
  const [signaturePassword, setSignaturePassword] = useState<string>("");
  const [signaturePasswordModified, setSignaturePasswordModified] =
    useState(false);
  const [showSignaturePassword, setShowSignaturePassword] = useState(false);

  const loadAssets = useCallback(async () => {
    setLoading(true);
    const result = await getTaxPayerAssets();
    if (result.status === SUCCESS && result.data) {
      setAssets(result.data);
      setSignaturePassword(result.data.signaturePassword ?? "");
    } else if (result.status === "not_found") {
      setAssets(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  const uploadFile = async (
    file: File,
    getUrlFn: () => Promise<{ url: string } | null>,
  ): Promise<boolean> => {
    const urlResult = await getUrlFn();
    if (!urlResult) return false;

    const uploadResponse = await fetch(putObjectMinioUrl(urlResult.url), {
      method: "PUT",
      body: file,
      headers: { "Content-type": file.type },
    });
    return uploadResponse.status === 200;
  };

  const downloadAsset = async (
    stagedFile: File | null | undefined,
    currentFileName: string | null | undefined,
    getUrlFn: () => Promise<{ status: string; data?: { url: string } }>,
  ) => {
    try {
      if (stagedFile) {
        triggerBlobDownload(stagedFile, stagedFile.name);
        return;
      }

      if (!currentFileName) return;
      const result = await getUrlFn();
      if (result.status !== SUCCESS || !result.data) {
        throw new Error("No se pudo obtener el archivo");
      }

      const response = await fetch(getObjectMinioUrl(result.data.url));
      if (!response.ok) {
        throw new Error("No se pudo descargar el archivo");
      }

      triggerBlobDownload(await response.blob(), currentFileName);
    } catch (_error) {
      errorAlert("No se pudo descargar el archivo");
    }
  };

  const deleteSavedAsset = async (
    field: TaxPayerAssetField,
    label: string,
  ) => {
    if (
      !window.confirm(
        `¿Eliminar ${label}? Esta acción quitará el archivo actualmente guardado.`,
      )
    ) {
      return;
    }

    const result = await deleteTaxPayerAsset(field);
    if (result.status !== SUCCESS) {
      errorAlert("No se pudo eliminar el archivo");
      return;
    }

    setAssets((prev) => (prev ? { ...prev, [field]: null } : prev));

    if (field === "signaturePath") {
      setSignaturePassword("");
      setSignaturePasswordModified(false);
      setAssets((prev) =>
        prev
          ? {
              ...prev,
              signaturePath: null,
              signaturePassword: null,
              signatureExpirationDate: null,
            }
          : prev,
      );
    } else if (field === "logoPath") {
      setLogoPreview(null);
    }

    successAlert("Archivo eliminado correctamente");
  };

  const handleSave = async () => {
    setSubmitting(true);

    try {
      // Stage and validate a new signature before replacing the current one.
      if (signatureFile.current) {
        const file = signatureFile.current;
        const uploadUrlResult = await createSignatureUploadUrl(file.name);
        if (uploadUrlResult.status !== SUCCESS || !uploadUrlResult.data) {
          errorAlert("Error al preparar la firma");
          return;
        }

        const uploadResponse = await fetch(
          putObjectMinioUrl(uploadUrlResult.data.url),
          {
            method: "PUT",
            body: file,
            headers: { "Content-type": file.type },
          },
        );
        if (uploadResponse.status !== 200) {
          errorAlert("Error al subir la firma");
          return;
        }

        const validationResult = await updateSignaturePassword(
          signaturePassword,
          {
            uploadId: uploadUrlResult.data.uploadId,
            fileName: file.name,
          },
        );
        if (validationResult.status !== SUCCESS) {
          errorAlert(getRequestDetail(validationResult));
          return;
        }

        setAssets((prev) => ({
          ...(prev ?? {}),
          signaturePath: file.name,
          signatureExpirationDate:
            validationResult.data?.signatureExpirationDate ??
            prev?.signatureExpirationDate,
        }));
        signatureFile.current = null;
        setStagedSignatureName(null);
        setSignaturePasswordModified(false);
      } else if (signaturePasswordModified) {
        // A password-only change is validated against the current signature.
        const res = await updateSignaturePassword(signaturePassword);
        if (res.status !== SUCCESS) {
          errorAlert(getRequestDetail(res));
          return;
        }
        setAssets((prev) => ({
          ...(prev ?? {}),
          signatureExpirationDate:
            res.data?.signatureExpirationDate ?? prev?.signatureExpirationDate,
        }));
        setSignaturePasswordModified(false);
      }

      // Upload logo if staged
      if (logoFile.current) {
        const file = logoFile.current;
        const ok = await uploadFile(file, async () => {
          const res = await createLogoUploadUrl(file.name);
          if (res.status !== SUCCESS || !res.data) return null;
          return res.data;
        });
        if (!ok) {
          errorAlert("Error al subir el logo");
          setSubmitting(false);
          return;
        }
        logoFile.current = null;
        setStagedLogoName(null);
      }

      // Upload templates if staged
      for (const [field, file] of Object.entries(templateFiles.current) as [
        TemplateField,
        File,
      ][]) {
        const ok = await uploadFile(file, async () => {
          const res = await createTemplateUploadUrl(field, file.name);
          if (res.status !== SUCCESS || !res.data) return null;
          return res.data;
        });
        if (!ok) {
          errorAlert(`Error al subir la plantilla: ${field}`);
          setSubmitting(false);
          return;
        }
      }
      templateFiles.current = {};
      setStagedTemplateNames({});

      successAlert("Activos actualizados correctamente");
      await loadAssets();
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignatureDrop = (file: File) => {
    signatureFile.current = file;
    setStagedSignatureName(file.name);
    setSignaturePasswordModified(true);
  };

  const handleSignatureClear = () => {
    signatureFile.current = null;
    setStagedSignatureName(null);
    setSignaturePasswordModified(
      signaturePassword !== (assets?.signaturePassword ?? ""),
    );
  };

  const handleLogoDrop = (file: File) => {
    logoFile.current = file;
    setStagedLogoName(file.name);
    if (file.type.startsWith("image/")) {
      setLogoPreview(URL.createObjectURL(file));
    } else {
      setLogoPreview(null);
    }
  };

  const handleLogoClear = async () => {
    logoFile.current = null;
    setStagedLogoName(null);
    setLogoPreview(null);

    if (assets?.logoPath) {
      const res = await getLogoUrl();
      if (res.status === SUCCESS && res.data) {
        setLogoPreview(res.data.url);
      }
    }
  };

  const handleTemplateDrop = (field: TemplateField) => (file: File) => {
    templateFiles.current[field] = file;
    setStagedTemplateNames((prev) => ({ ...prev, [field]: file.name }));
  };

  const handleTemplateClear = (field: TemplateField) => () => {
    delete templateFiles.current[field];
    setStagedTemplateNames((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const loadLogoPreview = async () => {
    if (!assets?.logoPath) return;
    const res = await getLogoUrl();
    if (res.status === SUCCESS && res.data) {
      setLogoPreview(res.data.url);
    }
  };

  useEffect(() => {
    if (!loading) {
      loadLogoPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, assets]);

  if (loading) {
    return <Loading height="300px" />;
  }

  const hasChanges =
    signatureFile.current !== null ||
    signaturePasswordModified ||
    logoFile.current !== null ||
    Object.keys(templateFiles.current).length > 0;

  return (
    <CCard>
      <CCardHeader>
        <strong>Activos del Contribuyente</strong>
      </CCardHeader>
      <CCardBody>
        <CNav variant="tabs" role="tablist">
          <CNavItem>
            <CNavLink
              active={activeTab === "signature"}
              onClick={() => setActiveTab("signature")}
              style={{ cursor: "pointer" }}
            >
              Firma
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink
              active={activeTab === "logo"}
              onClick={() => setActiveTab("logo")}
              style={{ cursor: "pointer" }}
            >
              Logo
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink
              active={activeTab === "templates"}
              onClick={() => setActiveTab("templates")}
              style={{ cursor: "pointer" }}
            >
              Plantillas
            </CNavLink>
          </CNavItem>
        </CNav>

        <CTabContent style={{ paddingTop: "16px" }}>
          <CTabPane active={activeTab === "signature"}>
            <DropZone
              label="Firma digital (.p12)"
              accept=".p12"
              currentFileName={stagedSignatureName ?? assets?.signaturePath}
              onFileDrop={handleSignatureDrop}
              onDownload={() =>
                downloadAsset(
                  signatureFile.current,
                  assets?.signaturePath,
                  getSignatureUrl,
                )
              }
              onClear={stagedSignatureName ? handleSignatureClear : undefined}
              onDelete={
                assets?.signaturePath && !stagedSignatureName
                  ? () => deleteSavedAsset("signaturePath", "la firma digital")
                  : undefined
              }
            />
            <div style={{ marginTop: "12px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: 600,
                  marginBottom: "4px",
                }}
              >
                Contraseña de firma digital
              </label>
              <CInputGroup style={{ maxWidth: "320px" }}>
                <input
                  type={showSignaturePassword ? "text" : "password"}
                  className="form-control"
                  value={signaturePassword}
                  onChange={(e) => {
                    setSignaturePassword(e.target.value);
                    setSignaturePasswordModified(true);
                  }}
                  placeholder="Contraseña"
                />
                <CButton
                  type="button"
                  color="secondary"
                  variant="outline"
                  onClick={() => setShowSignaturePassword((v) => !v)}
                >
                  {showSignaturePassword ? (
                    <i className="fa fa-eye-slash" />
                  ) : (
                    <i className="fa fa-eye" />
                  )}
                </CButton>
              </CInputGroup>
              {stagedSignatureName ? (
                <p
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--theme-warning-text)",
                    marginTop: "8px",
                  }}
                >
                  La firma nueva se validará con esta contraseña al guardar.
                </p>
              ) : assets?.signatureExpirationDate ? (
                <p
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--theme-text-soft)",
                    marginTop: "8px",
                  }}
                >
                  Vencimiento de firma:{" "}
                  {formatUTCDateTime(
                    new Date(assets.signatureExpirationDate),
                  )}
                </p>
              ) : null}
            </div>
          </CTabPane>

          <CTabPane active={activeTab === "logo"}>
            <DropZone
              label="Logo"
              currentFileName={stagedLogoName ?? assets?.logoPath}
              currentPreviewUrl={logoPreview}
              accept="image/*"
              onFileDrop={handleLogoDrop}
              onDownload={() =>
                downloadAsset(logoFile.current, assets?.logoPath, getLogoUrl)
              }
              onClear={stagedLogoName ? handleLogoClear : undefined}
              onDelete={
                assets?.logoPath && !stagedLogoName
                  ? () => deleteSavedAsset("logoPath", "el logo")
                  : undefined
              }
            />
          </CTabPane>

          <CTabPane active={activeTab === "templates"}>
            <h6 style={{ marginBottom: "12px" }}>Plantillas de Email</h6>
            {EMAIL_TEMPLATE_FIELDS.map(({ field, label }) => (
              <DropZone
                key={field}
                label={label}
                currentFileName={
                  stagedTemplateNames[field] ?? assets?.[field] ?? undefined
                }
                onFileDrop={handleTemplateDrop(field)}
                onDownload={() =>
                  downloadAsset(
                    templateFiles.current[field],
                    assets?.[field],
                    () => getTemplateUrl(field),
                  )
                }
                onClear={
                  stagedTemplateNames[field]
                    ? handleTemplateClear(field)
                    : undefined
                }
                onDelete={
                  assets?.[field] && !stagedTemplateNames[field]
                    ? () => deleteSavedAsset(field, `la plantilla ${label}`)
                    : undefined
                }
              />
            ))}
            <h6 style={{ margin: "16px 0 12px" }}>Plantillas KUDE</h6>
            {KUDE_TEMPLATE_FIELDS.map(({ field, label }) => (
              <DropZone
                key={field}
                label={label}
                currentFileName={
                  stagedTemplateNames[field] ?? assets?.[field] ?? undefined
                }
                onFileDrop={handleTemplateDrop(field)}
                onDownload={() =>
                  downloadAsset(
                    templateFiles.current[field],
                    assets?.[field],
                    () => getTemplateUrl(field),
                  )
                }
                onClear={
                  stagedTemplateNames[field]
                    ? handleTemplateClear(field)
                    : undefined
                }
                onDelete={
                  assets?.[field] && !stagedTemplateNames[field]
                    ? () => deleteSavedAsset(field, `la plantilla ${label}`)
                    : undefined
                }
              />
            ))}
          </CTabPane>
        </CTabContent>

        <div style={{ marginTop: "16px" }}>
          <CButton
            color="primary"
            onClick={handleSave}
            disabled={submitting || !hasChanges}
          >
            {submitting ? (
              <>
                <Spinner size="sm" animation="border" className="mr-1" />
                Guardando...
              </>
            ) : (
              "Guardar"
            )}
          </CButton>
        </div>
      </CCardBody>
    </CCard>
  );
};

export default TaxPayerAssetsForm;
